import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetTicketsQuery, useCloseTicketMutation } from '../../store/api/doctorApi';
import PageLoader from '../../components/PageLoader';
import { formatDateTime } from '../../utils/helpers';
import { showSuccess, showError } from '../../utils/toast';
import { confirmAction } from '../../utils/confirm';

const DoctorTickets: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'open' | 'closed'>('open');
  const { data, isLoading, error, refetch } = useGetTicketsQuery(undefined);
  const [closeTicket, { isLoading: isClosing }] = useCloseTicketMutation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
        <div className="alert alert-danger m-5">Error loading tickets</div>
      </div>
    );
  }

  const openTickets = data?.data?.open_tickets?.data || [];
  const closeTickets = data?.data?.close_tickets?.data || [];
  const tickets = activeTab === 'open' ? openTickets : closeTickets;

  const handleCloseTicket = async (ticketId: number) => {
    const confirmed = await confirmAction(
      'Close Ticket',
      'Are you sure you want to mark this ticket as closed?',
      'Yes, Close'
    );
    
    if (confirmed) {
      try {
        const result = await closeTicket(ticketId).unwrap();
        showSuccess(result.message || 'Ticket closed successfully!');
        refetch();
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to close ticket. Please try again.');
      }
    }
  };

  const formatTicketDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      const minutesStr = minutes.toString().padStart(2, '0');
      const day = date.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${hours12}:${minutesStr} ${ampm} ${day}, ${month} ${year}`;
    } catch {
      return formatDateTime(dateString);
    }
  };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Tickets</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Tickets</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-fluid">
            <div className="row justify-content-center">
              <div className="col-12 col-xl-10">
                <div className="card card-flush">
              <div className="card-header pt-5">
                <div className="card-title">
                  <ul className="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bold">
                    <li className="nav-item mt-2">
                      <button
                        className={`nav-link text-active-primary ms-0 me-10 ${activeTab === 'open' ? 'active' : ''}`}
                        onClick={() => setActiveTab('open')}
                        style={{ 
                          borderBottom: activeTab === 'open' ? '2px solid' : 'none',
                          borderColor: activeTab === 'open' ? '#50CD89' : 'transparent',
                          color: activeTab === 'open' ? '#50CD89' : '#A1A5B7',
                          cursor: 'pointer'
                        }}
                      >
                        <span className="svg-icon svg-icon-2 me-1">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16 17V19C16 19.6 15.6 20 15 20H5C4.4 20 4 19.6 4 19V7C4 6.4 4.4 6 5 6H9C9.6 6 10 6.4 10 7V9C10 9.6 10.4 10 11 10H15C15.6 10 16 10.4 16 11V17Z" fill={activeTab === 'open' ? '#50CD89' : '#A1A5B7'} />
                            <circle cx="12" cy="12" r="1.5" fill="white" />
                          </svg>
                        </span>
                        Open Tickets
                      </button>
                    </li>
                    <li className="nav-item mt-2">
                      <button
                        className={`nav-link text-active-primary ms-0 me-10 ${activeTab === 'closed' ? 'active' : ''}`}
                        onClick={() => setActiveTab('closed')}
                        style={{ 
                          borderBottom: activeTab === 'closed' ? '2px solid' : 'none',
                          borderColor: activeTab === 'closed' ? '#50CD89' : 'transparent',
                          color: activeTab === 'closed' ? '#50CD89' : '#A1A5B7',
                          cursor: 'pointer'
                        }}
                      >
                        <span className="svg-icon svg-icon-2 me-1">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16 17V19C16 19.6 15.6 20 15 20H5C4.4 20 4 19.6 4 19V7C4 6.4 4.4 6 5 6H9C9.6 6 10 6.4 10 7V9C10 9.6 10.4 10 11 10H15C15.6 10 16 10.4 16 11V17Z" fill={activeTab === 'closed' ? '#50CD89' : '#A1A5B7'} />
                            <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                        Closed Tickets
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th>Ticket Id</th>
                        <th>Subject</th>
                        <th>User</th>
                        <th>First Open By</th>
                        <th>Opened At</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {tickets.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-10">
                            <span className="text-muted">No {activeTab === 'open' ? 'open' : 'closed'} tickets found</span>
                          </td>
                        </tr>
                      ) : (
                        tickets.map((ticket: any) => (
                          <tr key={ticket.id}>
                            <td>#{ticket.id}</td>
                            <td>{ticket.subject || 'N/A'}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                {ticket.user?.picture && (
                                  <img src={ticket.user.picture} alt={ticket.user?.name || 'User'} className="w-30px h-30px rounded-circle me-3" />
                                )}
                                <div>
                                  <div className="fw-bold">{ticket.user?.name || 'N/A'}</div>
                                  <div className="text-muted fs-7">{ticket.user?.email || ''}</div>
                                </div>
                              </div>
                            </td>
                            <td>{ticket.first_open_by || 'N/A'}</td>
                            <td>{formatTicketDate(ticket.created_at || ticket.opened_at)}</td>
                            <td>
                              <div className="d-flex gap-2">
                                <button
                                  className="btn btn-sm btn-light-success"
                                  onClick={() => navigate(`/doctor/ticket/${ticket.id}`)}
                                  title="View Messages"
                                >
                                  <span className="svg-icon svg-icon-2 me-1">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4Z" fill="currentColor" />
                                      <path opacity="0.3" d="M12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6Z" fill="currentColor" />
                                      <path d="M12 10C13.1046 10 14 10.8954 14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12C10 10.8954 10.8954 10 12 10Z" fill="white" />
                                    </svg>
                                  </span>
                                  View Messages
                                </button>
                                {activeTab === 'open' && (
                                  <button
                                    className="btn btn-sm btn-light-success"
                                    onClick={() => handleCloseTicket(ticket.id)}
                                    disabled={isClosing}
                                    title="Mark as Closed"
                                  >
                                    <span className="svg-icon svg-icon-2">
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path opacity="0.3" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="currentColor" />
                                      </svg>
                                    </span>
                                    Mark as Closed
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorTickets;
